import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  User, Mail, Lock, Phone, MapPin, Briefcase, Calendar,
  GraduationCap, Linkedin, DollarSign, ArrowLeft, ArrowRight,
  Eye, EyeOff, Loader2, Search, Award, FileText, Upload,
} from 'lucide-react';
import './CandidateAuth.css';

const indianLocations = [
  'Bangalore','Mumbai','Delhi','Chennai','Hyderabad','Pune','Kolkata','Ahmedabad','Jaipur',
  'Lucknow','Nagpur','Indore','Bhopal','Coimbatore','Chandigarh','Visakhapatnam','Vadodara',
  'Ludhiana','Nashik','Patna','Surat','Ranchi','Guwahati','Raipur','Amritsar','Noida',
  'Gurgaon','Mysore','Thiruvananthapuram','Jodhpur',
];

/* ─── Password Input ─── */
function PwInput({ name, placeholder, value, onChange, icon: Icon = Lock }) {
  const [show, setShow] = useState(false);
  return (
    <div className="cnd-iw">
      <Icon size={18} className="cnd-iw-icon" />
      <input
        className="cnd-input"
        type={show ? 'text' : 'password'}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        autoComplete="off"
      />
      <button
        type="button"
        className="cnd-pw-toggle"
        onClick={() => setShow(!show)}
        tabIndex={-1}
        aria-label={show ? 'Hide' : 'Show'}
      >
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
      <span className="cnd-fline" />
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
const CandidateAuth = ({ onLoginSuccess, onBack }) => {
  const navigate = useNavigate();
const location = useLocation();
const view = location.pathname.endsWith('/register') ? 'register' : 'login';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [animKey, setAnimKey] = useState(0);

  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const [regData, setRegData] = useState({
    username: '', email: '', password: '', fullName: '',
    phone: '', location: '', skills: '', experience: '',
    relevantExperience: '', presentOrganization: '', linkedin: '', resume: null,
    role: '', photo: null, photoPreview: null,
    servingNotice: '', lastWorkingDay: '',
    currentCTC: '', currentLocation: '', preferredLocation: [],
  });

  const photoInputRef = useRef(null);
  const leftRef = useRef(null);
  const partRef = useRef(null);
  const emberRef = useRef(null);

  useEffect(() => {
  setAnimKey((k) => k + 1);
  setError('');
  setSuccessMessage('');
}, [view]);

const switchView = useCallback((v) => {
  navigate(v === 'register' ? '/candidate/register' : '/candidate');
}, [navigate]);

  /* ── Particles ── */
  useEffect(() => {
    const c = partRef.current;
    if (!c) return;
    const ps = [];
    for (let i = 0; i < 25; i++) {
      const p = document.createElement('div');
      p.className = 'cnd-pt';
      const sz = Math.random() * 1.8 + .8;
      p.style.cssText = `left:${Math.random()*100}%;bottom:${Math.random()*-15}%;width:${sz}px;height:${sz}px;animation-duration:${Math.random()*18+14}s;animation-delay:${Math.random()*12}s;`;
      c.appendChild(p); ps.push(p);
    }
    return () => ps.forEach((p) => p.remove());
  }, []);

  /* ── Embers (orange glowing dots) ── */
  useEffect(() => {
    const c = emberRef.current;
    if (!c) return;
    const es = [];
    for (let i = 0; i < 12; i++) {
      const e = document.createElement('div');
      e.className = 'cnd-ember';
      const sz = Math.random() * 3 + 1.5;
      const hue = Math.random() > .5 ? 'rgba(251,146,60,.7)' : 'rgba(245,158,11,.6)';
      e.style.cssText = `left:${Math.random()*100}%;bottom:${Math.random()*-10}%;width:${sz}px;height:${sz}px;background:${hue};box-shadow:0 0 ${sz*3}px ${hue};animation-duration:${Math.random()*16+10}s;animation-delay:${Math.random()*10}s;`;
      c.appendChild(e); es.push(e);
    }
    return () => es.forEach((e) => e.remove());
  }, []);

  /* ── Orb parallax ── */
  const onMouse = useCallback((e) => {
    const l = leftRef.current;
    if (!l) return;
    const r = l.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = (e.clientX - cx) / (r.width / 2);
    const dy = (e.clientY - cy) / (r.height / 2);
    l.querySelectorAll('.cnd-orb').forEach((o, i) => {
      const sp = (i + 1) * 12;
      o.style.transform = `translate(${dx * sp}px,${dy * sp}px)`;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouse);
    return () => window.removeEventListener('mousemove', onMouse);
  }, [onMouse]);

  /* ── Handlers (unchanged logic) ── */
  const handleLoginChange = (e) => setLoginData({ ...loginData, [e.target.name]: e.target.value });

  const handleRegChange = (e) => {
    const { name, value } = e.target;
    setRegData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Image must be < 2MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => setRegData((prev) => ({ ...prev, photo: file, photoPreview: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) setRegData((prev) => ({ ...prev, resume: file }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    let updated = [...regData.preferredLocation];
    if (checked) {
      if (updated.length < 3) updated.push(value);
      else alert('Max 3 locations allowed');
    } else {
      updated = updated.filter((loc) => loc !== value);
    }
    setRegData((prev) => ({ ...prev, preferredLocation: updated }));
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/candidate/login`,
        loginData
      );
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('candidateId', response.data.candidateId);
      localStorage.setItem('userType', 'candidate');
      if (onLoginSuccess) onLoginSuccess();
    } catch (err) {
      console.error('Login Error:', err);
      setError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    const formData = new FormData();
    Object.keys(regData).forEach((key) => {
      if (key === 'preferredLocation') {
        regData.preferredLocation.forEach((loc) => formData.append('preferredLocation', loc));
      } else if (key === 'photoPreview') {
        return;
      } else {
        if (regData[key] !== null && regData[key] !== undefined) {
          formData.append(key, regData[key]);
        }
      }
    });
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/candidate/register`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setSuccessMessage('Registration successful! Redirecting to login...');
      setTimeout(() => {
        setSuccessMessage('');
        switchView('login');
      }, 2000);
    } catch (err) {
      console.error('Registration Error:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const subtitles = {
    login: 'Login to access jobs and track applications.',
    register: 'Create your profile to find your dream job.',
  };

  return (
    <div className="cnd-w">
      {/* ━━━ LEFT PANEL ━━━ */}
      <div className="cnd-left" ref={leftRef}>
        <div className="cnd-left-grid" />
        <div className="cnd-orb cnd-orb--1" />
        <div className="cnd-orb cnd-orb--2" />
        <div className="cnd-orb cnd-orb--3" />
        <div className="cnd-parts" ref={partRef} />
        <div className="cnd-embers" ref={emberRef} />
        <div className="cnd-shapes">
          <div className="cnd-sh cnd-sh--1" />
          <div className="cnd-sh cnd-sh--2" />
          <div className="cnd-sh cnd-sh--3" />
          <div className="cnd-sh cnd-sh--4" />
        </div>
        <div className="cnd-diamonds">
          <div className="cnd-diamond cnd-diamond--1" />
          <div className="cnd-diamond cnd-diamond--2" />
          <div className="cnd-diamond cnd-diamond--3" />
        </div>
        <div className="cnd-hexes">
          <div className="cnd-hex cnd-hex--1" />
          <div className="cnd-hex cnd-hex--2" />
        </div>

        <div className="cnd-left-content">
          <div className="cnd-left-brand">
            <div className="cnd-brand-box">
              <GraduationCap size={24} color="#fff" />
            </div>
            <span className="cnd-brand-name">TalentOza</span>
          </div>

          <h1 className="cnd-left-h">
            Candidate<br />
            <span className="cnd-gr">Portal</span>
          </h1>

          <p className="cnd-left-p">
            Create your profile, discover opportunities, apply for
            jobs, take assessments, and track your application status.
          </p>

          <div className="cnd-left-feats">
            <div className="cnd-left-feat">
              <div className="cnd-feat-icon">
                <Search size={17} color="#fb923c" />
              </div>
              <div className="cnd-feat-text">
                <h4>Discover Jobs</h4>
                <p>Browse thousands of curated opportunities</p>
              </div>
            </div>
            <div className="cnd-left-feat">
              <div className="cnd-feat-icon">
                <FileText size={17} color="#fbbf24" />
              </div>
              <div className="cnd-feat-text">
                <h4>Smart Profiles</h4>
                <p>One profile, auto-matched to relevant roles</p>
              </div>
            </div>
            <div className="cnd-left-feat">
              <div className="cnd-feat-icon">
                <Award size={17} color="#fed7aa" />
              </div>
              <div className="cnd-feat-text">
                <h4>Skill Assessments</h4>
                <p>Prove your skills with verified tests</p>
              </div>
            </div>
            <div className="cnd-left-feat">
              <div className="cnd-feat-icon">
                <Briefcase size={17} color="#fdba74" />
              </div>
              <div className="cnd-feat-text">
                <h4>Track Applications</h4>
                <p>Real-time status on every submission</p>
              </div>
            </div>
          </div>
        </div>

        <div className="cnd-left-bottom">
          <div className="cnd-trust">
            <span className="cnd-trust-dot" />
            <span className="cnd-trust-text">
              <strong>10K+ job seekers</strong> · Quick apply · Instant matches
            </span>
          </div>
        </div>
      </div>

      {/* ━━━ RIGHT PANEL ━━━ */}
      <div className="cnd-right">
        {onBack && (
          <button className="cnd-back" onClick={onBack}>
            <ArrowLeft size={16} /> Back
          </button>
        )}

        <div className={`cnd-form-wrap ${view === 'register' ? 'cnd-form-wrap--wide' : ''}`}>
          {/* Header */}
          <div className="cnd-header">
            <div className="cnd-icon-wrap">
              <div className="cnd-icon-box">
                <GraduationCap size={28} color="var(--c-pri)" />
              </div>
              <div className="cnd-lock-badge">
                <Lock size={11} color="#fff" />
              </div>
            </div>
            <h1 className="cnd-h1">
              {view === 'login' ? 'Candidate Portal' : 'Create Profile'}
            </h1>
            <p className="cnd-sub">{subtitles[view]}</p>
            <div className="cnd-sec-badge">
              <span className="cnd-sec-dot" />
              Secure Connection
            </div>
          </div>

          {/* Banners */}
          {error && <div className="cnd-banner cnd-banner--error">{error}</div>}
          {successMessage && <div className="cnd-banner cnd-banner--success">{successMessage}</div>}

          {/* ━━ FORMS ━━ */}
          <div className="cnd-view" key={animKey}>

            {/* ── LOGIN ── */}
            {view === 'login' && (
              <form className="cnd-form" onSubmit={handleLoginSubmit}>
                <div className="cnd-fg">
                  <label className="cnd-label">Email Address</label>
                  <div className="cnd-iw">
                    <Mail size={18} className="cnd-iw-icon" />
                    <input className="cnd-input" type="email" name="email" value={loginData.email} onChange={handleLoginChange} placeholder="Enter your email" required autoComplete="off" />
                    <span className="cnd-fline" />
                  </div>
                </div>

                <div className="cnd-fg">
                  <label className="cnd-label">Password</label>
                  <PwInput name="password" placeholder="Enter your password" value={loginData.password} onChange={handleLoginChange} />
                </div>

                <button type="submit" className={`cnd-btn ${loading ? 'cnd-btn--loading' : ''}`} disabled={loading}>
                  {loading ? <Loader2 size={20} className="cnd-spinner" /> : <><span>Login</span><ArrowRight size={18} /></>}
                </button>

                <div className="cnd-form-footer">
                  <span>Don't have an account?</span>
                  <button type="button" className="cnd-footer-link" onClick={() => switchView('register')}>Register Now</button>
                </div>
              </form>
            )}

            {/* ── REGISTER ── */}
            {view === 'register' && (
              <form className="cnd-form" onSubmit={handleRegisterSubmit}>

                {/* Photo */}
                <div className="cnd-photo-section">
                  <div className="cnd-photo-circle" onClick={() => photoInputRef.current.click()}>
                    {regData.photoPreview
                      ? <img src={regData.photoPreview} alt="Preview" />
                      : <span>Photo</span>}
                  </div>
                  <input type="file" ref={photoInputRef} onChange={handlePhotoChange} style={{ display: 'none' }} accept="image/*" />
                  <button type="button" className="cnd-photo-btn" onClick={() => photoInputRef.current.click()}>
                    <Upload size={14} /> Upload Photo
                  </button>
                </div>

                <div className="cnd-section-title">Basic Information</div>

                <div className="cnd-grid2">
                  <div className="cnd-fg">
                    <label className="cnd-label">Username</label>
                    <div className="cnd-iw"><User size={18} className="cnd-iw-icon" /><input className="cnd-input" name="username" value={regData.username} onChange={handleRegChange} required placeholder="Username" /><span className="cnd-fline" /></div>
                  </div>
                  <div className="cnd-fg">
                    <label className="cnd-label">Full Name</label>
                    <div className="cnd-iw"><User size={18} className="cnd-iw-icon" /><input className="cnd-input" name="fullName" value={regData.fullName} onChange={handleRegChange} required placeholder="Full Name" /><span className="cnd-fline" /></div>
                  </div>
                  <div className="cnd-fg">
                    <label className="cnd-label">Email</label>
                    <div className="cnd-iw"><Mail size={18} className="cnd-iw-icon" /><input className="cnd-input" type="email" name="email" value={regData.email} onChange={handleRegChange} required placeholder="Email" /><span className="cnd-fline" /></div>
                  </div>
                  <div className="cnd-fg">
                    <label className="cnd-label">Password</label>
                    <PwInput name="password" placeholder="Password" value={regData.password} onChange={handleRegChange} />
                  </div>
                  <div className="cnd-fg">
                    <label className="cnd-label">Phone</label>
                    <div className="cnd-iw"><Phone size={18} className="cnd-iw-icon" /><input className="cnd-input" name="phone" value={regData.phone} onChange={handleRegChange} required placeholder="Phone" /><span className="cnd-fline" /></div>
                  </div>
                  <div className="cnd-fg">
                    <label className="cnd-label">Role</label>
                    <div className="cnd-iw"><Briefcase size={18} className="cnd-iw-icon" /><input className="cnd-input" name="role" value={regData.role} onChange={handleRegChange} required placeholder="e.g. Developer" /><span className="cnd-fline" /></div>
                  </div>
                </div>

                <div className="cnd-section-title">Experience & Compensation</div>

                <div className="cnd-grid2">
                  <div className="cnd-fg">
                    <label className="cnd-label">Total Experience</label>
                    <input className="cnd-input cnd-input--plain" name="experience" value={regData.experience} onChange={handleRegChange} placeholder="e.g. 2 Years" />
                  </div>
                  <div className="cnd-fg">
                    <label className="cnd-label">Relevant Experience</label>
                    <input className="cnd-input cnd-input--plain" name="relevantExperience" value={regData.relevantExperience} onChange={handleRegChange} placeholder="e.g. 1 Year" />
                  </div>
                  <div className="cnd-fg">
                    <label className="cnd-label">Current CTC</label>
                    <div className="cnd-iw"><DollarSign size={18} className="cnd-iw-icon" /><input className="cnd-input" name="currentCTC" value={regData.currentCTC} onChange={handleRegChange} placeholder="LPA" /><span className="cnd-fline" /></div>
                  </div>
                  <div className="cnd-fg">
                    <label className="cnd-label">Current Location</label>
                    <div className="cnd-iw"><MapPin size={18} className="cnd-iw-icon" /><input className="cnd-input" name="currentLocation" value={regData.currentLocation} onChange={handleRegChange} placeholder="City" /><span className="cnd-fline" /></div>
                  </div>
                </div>

                <div className="cnd-section-title">Skills & Links</div>

                <div className="cnd-fg">
                  <label className="cnd-label">Skills</label>
                  <input className="cnd-input cnd-input--plain" name="skills" value={regData.skills} onChange={handleRegChange} placeholder="Java, React, Python..." required />
                </div>

                <div className="cnd-fg">
                  <label className="cnd-label">LinkedIn URL</label>
                  <div className="cnd-iw"><Linkedin size={18} className="cnd-iw-icon" /><input className="cnd-input" name="linkedin" value={regData.linkedin} onChange={handleRegChange} placeholder="https://linkedin.com/in/..." /><span className="cnd-fline" /></div>
                </div>

                <div className="cnd-fg">
                  <label className="cnd-label">Upload Resume (PDF/DOC)</label>
                  <div className="cnd-file-wrap">
                    <input type="file" name="resume" onChange={handleResumeChange} accept=".pdf,.doc,.docx" required />
                  </div>
                </div>

                <div className="cnd-section-title">Availability</div>

                <div className="cnd-fg">
                  <label className="cnd-label">Serving Notice Period?</label>
                  <div className="cnd-radio-group">
                    <label className="cnd-radio-label"><input type="radio" name="servingNotice" value="Yes" checked={regData.servingNotice === 'Yes'} onChange={handleRegChange} /> Yes</label>
                    <label className="cnd-radio-label"><input type="radio" name="servingNotice" value="No" checked={regData.servingNotice === 'No'} onChange={handleRegChange} /> No</label>
                  </div>
                </div>

                {regData.servingNotice === 'Yes' && (
                  <div className="cnd-fg">
                    <label className="cnd-label">Last Working Day</label>
                    <div className="cnd-iw"><Calendar size={18} className="cnd-iw-icon" /><input className="cnd-input" type="date" name="lastWorkingDay" value={regData.lastWorkingDay} onChange={handleRegChange} /><span className="cnd-fline" /></div>
                  </div>
                )}

                <div className="cnd-fg">
                  <label className="cnd-label">Preferred Locations (Max 3)</label>
                  <div className="cnd-check-grid">
                    {indianLocations.map((loc) => (
                      <label key={loc} className="cnd-check-label">
                        <input type="checkbox" value={loc} checked={regData.preferredLocation.includes(loc)} onChange={handleCheckboxChange} />
                        {loc}
                      </label>
                    ))}
                  </div>
                </div>

                <button type="submit" className={`cnd-btn ${loading ? 'cnd-btn--loading' : ''}`} disabled={loading}>
                  {loading ? <Loader2 size={20} className="cnd-spinner" /> : <><span>Register</span><ArrowRight size={18} /></>}
                </button>

                <div className="cnd-form-footer">
                  <span>Already registered?</span>
                  <button type="button" className="cnd-footer-link" onClick={() => switchView('login')}>Log In</button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateAuth;